import type { HubChromeOpportunityRow } from "./hub-chrome.types.js";

/** Server group key for opportunities that have no persona. */
export const HUB_NO_PERSONA_GROUP_KEY = "__no_persona";
import type {
  HubChromeSet,
  OpportunityHubGroupBy,
  OpportunityHubSelectionMix,
  OpportunityHubSetTypeFilter,
} from "./hub-chrome.types.js";

/** Post-Flight table chrome from set scope and filters — not from partially loaded opportunity rows. */
export function isPostFlightOnlyHubView(
  opportunityTypeFilter: OpportunityHubSetTypeFilter,
  setsInScope: readonly HubChromeSet[],
): boolean {
  if (opportunityTypeFilter === "POST_FLIGHT") return true;
  if (opportunityTypeFilter !== "all") return false;
  if (setsInScope.length === 0) return false;
  return setsInScope.every((set) => set.type === "POST_FLIGHT");
}

export function selectionMixFromSets(sets: HubChromeSet[]): OpportunityHubSelectionMix {
  return {
    hasPersona: sets.some((set) => set.type === "PERSONA"),
    hasCompetitor: sets.some((set) => set.type === "COMPETITOR"),
  };
}

export function defaultGroupBy(mix: OpportunityHubSelectionMix): OpportunityHubGroupBy {
  if (mix.hasPersona && mix.hasCompetitor) return "type";
  if (mix.hasPersona) return "persona";
  return "angle";
}

export function groupByOptionsForMix(mix: OpportunityHubSelectionMix): OpportunityHubGroupBy[] {
  const options: OpportunityHubGroupBy[] = [];
  if (mix.hasPersona) options.push("persona");
  options.push("angle");
  options.push("set");
  if (mix.hasPersona && mix.hasCompetitor) options.push("type");
  if (mix.hasCompetitor) options.push("feature");
  return options;
}

export function resolveGroupBy(
  mix: OpportunityHubSelectionMix,
  current: OpportunityHubGroupBy,
): OpportunityHubGroupBy {
  const allowed = groupByOptionsForMix(mix);
  return allowed.includes(current) ? current : defaultGroupBy(mix);
}

/** Server rollup for a collapsed header. Absent when every child row is already in `rows`. */
export interface OpportunityHubGroupRollup {
  count: number;
  spend: number;
  creativesCount: number;
  competitorsRunningCount: number;
  competitorsTotal: number;
  stickRatePct: number | null;
  duplicatedPct: number | null;
  investmentIndex: number | null;
}

export interface OpportunityHubGroup {
  key: string;
  /** Raw group value — `PERSONA` / `COMPETITOR` tokens are localised at render. */
  title: string;
  avatarUrl: string | null;
  rows: HubChromeOpportunityRow[];
  rollup?: OpportunityHubGroupRollup;
  who?: string | null;
  personalityKey?: string | null;
  pains?: string[];
  motivations?: string[];
  isStale?: boolean;
  /** Set this group belongs to. Absent when its rows span more than one set. */
  setId?: string | null;
  setName?: string | null;
}

export function isNoPersonaGroup(group: { key: string }): boolean {
  return group.key === HUB_NO_PERSONA_GROUP_KEY;
}

/** The bare table: the only group is opportunities with no persona. */
export function isBareNoPersonaList(groups: readonly { key: string }[]): boolean {
  return groups.length === 1 && isNoPersonaGroup(groups[0]!);
}

/** `__no_persona` sorts first by key. Persona folds stay in their existing order; it goes last. */
export function placeNoPersonaGroupLast<T extends { key: string }>(groups: readonly T[]): T[] {
  const personas: T[] = [];
  const noPersona: T[] = [];
  for (const group of groups) {
    if (isNoPersonaGroup(group)) noPersona.push(group);
    else personas.push(group);
  }
  return noPersona.length === 0 ? [...groups] : [...personas, ...noPersona];
}

function groupIdentity(
  groupBy: OpportunityHubGroupBy,
  row: HubChromeOpportunityRow,
): { key: string; title: string; avatarUrl: string | null } {
  if (groupBy === "persona") {
    return {
      key: row.personaName ?? "__no_persona",
      title: row.personaName ?? "",
      avatarUrl: row.personaAvatarUrl,
    };
  }
  if (groupBy === "set") {
    return { key: row.setId, title: row.setName, avatarUrl: null };
  }
  if (groupBy === "type") {
    return { key: row.setType, title: row.setType, avatarUrl: null };
  }
  if (groupBy === "feature") {
    const feature = row.feature ?? row.angle;
    return { key: feature, title: feature, avatarUrl: null };
  }
  return { key: row.angle, title: row.angle, avatarUrl: null };
}

export function groupOpportunityRows(
  rows: HubChromeOpportunityRow[],
  groupBy: OpportunityHubGroupBy,
): OpportunityHubGroup[] {
  const groups: OpportunityHubGroup[] = [];
  const indexByKey = new Map<string, number>();

  for (const row of rows) {
    const { key, title, avatarUrl } = groupIdentity(groupBy, row);
    const existing = indexByKey.get(key);
    if (existing == null) {
      indexByKey.set(key, groups.length);
      groups.push({ key, title, avatarUrl, rows: [row], setId: row.setId, setName: row.setName });
    } else {
      const group = groups[existing]!;
      group.rows.push(row);
      if (group.setId !== row.setId) {
        group.setId = null;
        group.setName = null;
      }
    }
  }

  return groups;
}
